import React, { useCallback, useEffect, useState } from 'react';
import { Plus, X, Trash2, Loader2, Percent, Play } from 'lucide-react';
import { apiClient } from '../../services/api';
import { toast } from '../../hooks/use-toast';

const api = {
  list: () => apiClient.get('/hr/tax-slabs').then((r) => r.data),
  create: (p) => apiClient.post('/hr/tax-slabs', p).then((r) => r.data),
  patch: (id, p) => apiClient.patch(`/hr/tax-slabs/${id}`, p).then((r) => r.data),
  del: (id) => apiClient.delete(`/hr/tax-slabs/${id}`).then((r) => r.data),
  seed: () => apiClient.post('/hr/tax-slabs/seed-defaults').then((r) => r.data),
  simulate: (p) => apiClient.post('/hr/tax-slabs/simulate', p).then((r) => r.data),
};

const COUNTRIES = [['IN', 'India'], ['UK', 'United Kingdom'], ['MY', 'Malaysia'], ['GENERIC', 'Other']];
const TAX_TYPES = ['TDS', 'PAYE', 'NI', 'PF', 'EPF', 'ESI', 'SOCSO', 'CESS'];
const REGIMES = ['DEFAULT', 'OLD', 'NEW'];

const emptySlab = () => ({ country: 'IN', regime: 'DEFAULT', tax_type: 'TDS', financial_year: '2025-26',
  slabs: [{ from_amount: 0, to_amount: null, rate_pct: 0 }], fixed_deductions: [], standard_deduction: 0, active: true, note: '' });

const TaxSlabsPage = () => {
  const [items, setItems] = useState([]);
  const [showEdit, setShowEdit] = useState(null); // null | new | object
  const [showSim, setShowSim] = useState(false);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => { setLoading(true); try { const r = await api.list(); setItems(r.data || []); } finally { setLoading(false); } }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="flex items-end justify-between gap-3 flex-wrap mb-4 sm:mb-6">
        <div className="min-w-0">
          <span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">Tax Slabs</span>
          <h1 className="mt-2 text-3xl sm:text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>Configurable tax brackets</h1>
          <p className="mt-1 text-xs sm:text-sm text-[#0b1a2e]/60">PAYE / TDS / NI / PF / EPF / ESI / SOCSO — used by the payroll engine.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={async () => { try { const r = await api.seed(); toast({ title: `Seeded ${r.inserted} slabs` }); load(); } catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); } }}
            data-testid="tax-seed-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-[#0f5c3d]/20 text-xs">Seed defaults</button>
          <button onClick={() => setShowSim(true)} data-testid="tax-sim-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-[#0f5c3d]/20 text-xs"><Play className="w-3.5 h-3.5" />Simulate</button>
          <button onClick={() => setShowEdit(emptySlab())} data-testid="tax-new-btn" className="inline-flex items-center gap-1 px-3 py-2 rounded-full bg-[#0f5c3d] text-white text-xs"><Plus className="w-3.5 h-3.5" /> New slab</button>
        </div>
      </div>
      {loading ? <div className="p-16 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#0f5c3d] inline" /></div> : (
        <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#f7fbf8]"><tr>
              <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Country</th>
              <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Type</th>
              <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Regime</th>
              <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">FY</th>
              <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Slabs</th>
              <th className="text-right p-2 text-xs uppercase text-[#0b1a2e]/60">Std deduction</th>
              <th></th>
            </tr></thead>
            <tbody>{items.map((s) => (
              <tr key={s.id} className="border-t border-black/5 hover:bg-[#f7fbf8]" data-testid={`slab-row-${s.id}`}>
                <td className="p-2">{s.country}</td>
                <td className="p-2 font-mono text-xs">{s.tax_type}</td>
                <td className="p-2 text-xs">{s.regime}</td>
                <td className="p-2 text-xs">{s.financial_year}</td>
                <td className="p-2 text-xs">{(s.slabs || []).map((r, i) => <span key={i} className="mr-2">{(r.from_amount / 1000).toFixed(0)}k-{r.to_amount ? (r.to_amount / 1000).toFixed(0) + 'k' : '∞'}: <b>{r.rate_pct}%</b></span>)}</td>
                <td className="p-2 text-right">{s.standard_deduction || 0}</td>
                <td className="p-2 text-right">
                  <button onClick={() => setShowEdit(s)} data-testid={`slab-edit-${s.id}`} className="text-[#0f5c3d] mr-2 text-xs">Edit</button>
                  <button onClick={async () => { if (window.confirm(`Delete ${s.tax_type} ${s.country} ${s.financial_year}?`)) { await api.del(s.id); load(); } }} className="text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                </td>
              </tr>
            ))}</tbody>
          </table>
          {items.length === 0 && <div className="p-16 text-center text-[#0b1a2e]/50 text-sm">No slabs yet. Click <b>Seed defaults</b>.</div>}
        </div>
      )}
      {showEdit && <SlabModal initial={showEdit} onClose={() => setShowEdit(null)} onSaved={() => { setShowEdit(null); load(); }} />}
      {showSim && <SimulateModal onClose={() => setShowSim(false)} />}
    </div>
  );
};

const SlabModal = ({ initial, onClose, onSaved }) => {
  const [f, setF] = useState(initial);
  const isEdit = !!f.id;
  const save = async () => {
    try {
      const p = { ...f, slabs: f.slabs.map((s) => ({ from_amount: parseFloat(s.from_amount || 0), to_amount: s.to_amount ? parseFloat(s.to_amount) : null, rate_pct: parseFloat(s.rate_pct || 0) })), standard_deduction: parseFloat(f.standard_deduction || 0) };
      if (isEdit) await api.patch(f.id, p); else await api.create(p);
      toast({ title: 'Saved' }); onSaved();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 bg-[#0b1a2e] text-white flex justify-between"><div className="font-medium">{isEdit ? 'Edit' : 'New'} tax slab</div><button onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-4 gap-2">
            <div><label className="text-[10px] uppercase font-semibold text-[#0b1a2e]/60">Country</label>
              <select value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} className="w-full px-3 py-2 border rounded text-sm bg-white">
                {COUNTRIES.map(([c, l]) => <option key={c} value={c}>{l}</option>)}
              </select></div>
            <div><label className="text-[10px] uppercase font-semibold text-[#0b1a2e]/60">Type</label>
              <select value={f.tax_type} onChange={(e) => setF({ ...f, tax_type: e.target.value })} className="w-full px-3 py-2 border rounded text-sm bg-white">
                {TAX_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select></div>
            <div><label className="text-[10px] uppercase font-semibold text-[#0b1a2e]/60">Regime</label>
              <select value={f.regime} onChange={(e) => setF({ ...f, regime: e.target.value })} className="w-full px-3 py-2 border rounded text-sm bg-white">
                {REGIMES.map((r) => <option key={r}>{r}</option>)}
              </select></div>
            <div><label className="text-[10px] uppercase font-semibold text-[#0b1a2e]/60">FY</label>
              <input value={f.financial_year} onChange={(e) => setF({ ...f, financial_year: e.target.value })} placeholder="2025-26" className="w-full px-3 py-2 border rounded text-sm" /></div>
          </div>
          <div><label className="text-[10px] uppercase font-semibold text-[#0b1a2e]/60">Standard deduction</label>
            <input type="number" value={f.standard_deduction || 0} onChange={(e) => setF({ ...f, standard_deduction: e.target.value })} className="w-full px-3 py-2 border rounded text-sm" /></div>
          <div>
            <label className="text-[10px] uppercase font-semibold text-[#0b1a2e]/60">Slabs (annual amounts, blank = ∞)</label>
            <div className="space-y-2 mt-1">
              {f.slabs.map((s, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input type="number" placeholder="from" value={s.from_amount || ''} onChange={(e) => { const sl = [...f.slabs]; sl[i] = { ...s, from_amount: e.target.value }; setF({ ...f, slabs: sl }); }} className="flex-1 px-2 py-1 border rounded text-sm" />
                  <input type="number" placeholder="to (blank=∞)" value={s.to_amount ?? ''} onChange={(e) => { const sl = [...f.slabs]; sl[i] = { ...s, to_amount: e.target.value || null }; setF({ ...f, slabs: sl }); }} className="flex-1 px-2 py-1 border rounded text-sm" />
                  <input type="number" step="0.1" placeholder="%" value={s.rate_pct || ''} onChange={(e) => { const sl = [...f.slabs]; sl[i] = { ...s, rate_pct: e.target.value }; setF({ ...f, slabs: sl }); }} className="w-20 px-2 py-1 border rounded text-sm" />
                  <button onClick={() => setF({ ...f, slabs: f.slabs.filter((_, ix) => ix !== i) })} className="text-red-600 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
              <button onClick={() => setF({ ...f, slabs: [...f.slabs, { from_amount: 0, to_amount: null, rate_pct: 0 }] })} className="text-xs text-[#0f5c3d] hover:underline">+ Add slab</button>
            </div>
          </div>
          <button onClick={save} data-testid="slab-save-btn" className="w-full mt-2 px-4 py-2 rounded-full bg-[#0f5c3d] text-white text-sm font-semibold">Save</button>
        </div>
      </div>
    </div>
  );
};

const SimulateModal = ({ onClose }) => {
  const [f, setF] = useState({ country: 'IN', regime: 'NEW', tax_type: 'TDS', financial_year: '2025-26', gross_annual: 1000000 });
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { setRes(await api.simulate({ ...f, gross_annual: parseFloat(f.gross_annual) })); }
    catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); setRes(null); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden">
        <div className="px-5 py-4 bg-[#0b1a2e] text-white flex justify-between"><div className="font-medium">Tax simulator</div><button onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <select value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} className="px-3 py-2 border rounded text-sm bg-white">{COUNTRIES.map(([c, l]) => <option key={c} value={c}>{l}</option>)}</select>
            <select value={f.tax_type} onChange={(e) => setF({ ...f, tax_type: e.target.value })} className="px-3 py-2 border rounded text-sm bg-white">{TAX_TYPES.map((t) => <option key={t}>{t}</option>)}</select>
            <select value={f.regime} onChange={(e) => setF({ ...f, regime: e.target.value })} className="px-3 py-2 border rounded text-sm bg-white">{REGIMES.map((r) => <option key={r}>{r}</option>)}</select>
            <input value={f.financial_year} onChange={(e) => setF({ ...f, financial_year: e.target.value })} className="px-3 py-2 border rounded text-sm" />
          </div>
          <div><label className="text-[10px] uppercase font-semibold text-[#0b1a2e]/60">Annual gross</label>
            <input type="number" value={f.gross_annual} onChange={(e) => setF({ ...f, gross_annual: e.target.value })} data-testid="sim-gross" className="w-full px-3 py-2 border rounded text-sm" /></div>
          <button onClick={run} disabled={busy} data-testid="sim-run-btn" className="w-full px-4 py-2 rounded-full bg-[#0f5c3d] text-white text-sm">{busy && <Loader2 className="w-3.5 h-3.5 inline animate-spin mr-1" />}Simulate</button>
          {res && (
            <div className="mt-3 bg-[#f7fbf8] p-3 rounded text-sm">
              <div className="flex justify-between"><span>Taxable income</span><b>{res.taxable_income.toLocaleString()}</b></div>
              <div className="flex justify-between mt-1"><span>Annual tax</span><b>{res.tax_annual.toLocaleString()}</b></div>
              <div className="flex justify-between mt-1 text-[#0f5c3d] font-bold"><span>Monthly deduction</span><span>{res.tax_monthly.toLocaleString()}</span></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaxSlabsPage;
